import { useEffect, useRef } from 'react';
import Phaser from 'phaser';

import { createGameConfig } from '@/features/letterParty/game/config';
import LetterPartyScene from '@/features/letterParty/game/scenes/LetterPartyScene';
import { syncPhaserCanvasSize } from '@/shared/utils/phaserCanvasSize';
import { createPhaserGameLifecycle } from '@/shared/utils/phaserGameLifecycle';

import type { RefObject } from 'react';
import type { LetterPartyTheme } from '@/features/letterParty/types';
import type { LetterPartyBoardState } from '@/features/letterParty/game/scenes/LetterPartyScene';

type UseLetterPartyPhaserGameParams = {
	boardState: LetterPartyBoardState;
	containerRef: RefObject<HTMLDivElement | null>;
	onSwipeDirection: (direction: LetterPartyBoardState['currentDirection']) => void;
	theme: LetterPartyTheme;
};

const gameLifecycle = createPhaserGameLifecycle();

export function useLetterPartyPhaserGame({
	boardState,
	containerRef,
	onSwipeDirection,
	theme,
}: UseLetterPartyPhaserGameParams): void {
	const gameRef = useRef<Phaser.Game | null>(null);
	const sceneRef = useRef<LetterPartyScene | null>(null);
	const boardStateRef = useRef(boardState);
	const onSwipeDirectionRef = useRef(onSwipeDirection);

	useEffect(() => {
		boardStateRef.current = boardState;
		sceneRef.current?.updateBoard(boardState);
	}, [boardState]);

	useEffect(() => {
		onSwipeDirectionRef.current = onSwipeDirection;
	}, [onSwipeDirection]);

	useEffect(() => {
		let isMounted = true;
		let handleResize: (() => void) | null = null;
		let resizeObserver: ResizeObserver | null = null;

		const mountGame = async () => {
			await gameLifecycle.waitForPendingDestroy();

			const container = containerRef.current;
			if (!isMounted || !container || gameRef.current) {
				return;
			}

			const scene = new LetterPartyScene((direction) => onSwipeDirectionRef.current(direction), theme);
			sceneRef.current = scene;

			const game = new Phaser.Game(createGameConfig(container, scene));
			gameRef.current = game;
			syncPhaserCanvasSize(game, container, { fallback: 'minimum' });
			scene.updateBoard(boardStateRef.current);

			handleResize = () => {
				if (gameRef.current && container.isConnected) {
					syncPhaserCanvasSize(gameRef.current, container, { fallback: 'minimum' });
					sceneRef.current?.updateBoard(boardStateRef.current);
				}
			};

			resizeObserver = new ResizeObserver(handleResize);
			resizeObserver.observe(container);
		};

		mountGame();

		return () => {
			isMounted = false;
			resizeObserver?.disconnect();

			if (gameRef.current) {
				gameLifecycle.destroy(gameRef.current);
			}

			gameRef.current = null;
			sceneRef.current = null;
		};
	}, [containerRef, theme]);
}
